import type { CourseToolId } from './courseTools'
import type { CourseId } from './courses'

export type StudentWorkExperience = {
  id: string
  jobTitle: string
  employerId: string
  time: {
    from: Date
    to: Date | string
  }
  descriptions?: string[]
}

export type StudentEducation = {
  id: string
  university: string
  level: string
  field: string
  time: {
    from: Date
    to: Date
  }
  grade?: string
  descriptions?: string[]
}

export type StudentAbout = {
  overview: {
    description?: string
    status?: string
    languages?: string[]
    permit?: string[]
    skills?: CourseToolId[]
  }
  contact: {
    location?: {
      line?: string
      city?: string
      postcode?: string
      state: string
      country: string
    }
    links?: {
      type: string
      url: string
    }[]
  }
  workExperiences: StudentWorkExperience[]
  educations: StudentEducation[]
}

export type StudentRankItem = {
  testId: string
  scores: number[]
  rank: number
}

export type StudentSkillMap = {
  title: string
  breakdown: {
    title: string
    score: number
  }[]
}

export type Student = {
  id: string
  firstName: string
  lastName: string
  email: string
  avatarUrl: string
  courses: CourseId[]
  certificates: string[]
  profile: {
    about: StudentAbout
    ranking: StudentRankItem[]
    skills: StudentSkillMap[]
  }
  settings: {
    path: string
    id: {
      type: string
      value: string
    }
  }
}

const filledOverview: StudentAbout['overview'] = {
  description: `As a recent Computer Science graduate with a strong foundation in web technologies, I am passionate about creating intuitive and dynamic user interfaces. With proficiency in HTML, CSS, and JavaScript, coupled with hands-on experience in modern frameworks like React and Vue.js, I am eager to contribute to innovative front-end projects and deliver exceptional user experiences.`,
  status: 'Open to opportunities',
  languages: ['English', 'Mandarin'],
  permit: ['Malaysia'],
  skills: [
    'html',
    'css',
    'javascript',
    'typescript',
    'github',
    'node',
    'express',
    'vue',
    'react',
    'next'
  ]
}

const filledWorkExperiences: StudentWorkExperience[] = [
  {
    id: 'work-experience-0',
    jobTitle: 'Junior Frontend Web Developer',
    employerId: 'accenture',
    time: {
      from: new Date('2018/01/20'),
      to: new Date('2023/10/20')
    },
    descriptions: [
      `Assisted in developing and maintaining responsive web applications, contributing to a 20% increase in user engagement and satisfaction.`
    ]
  },
  {
    id: 'work-experience-1',
    jobTitle: 'Senior Frontend Web Developer',
    employerId: 'faver',
    time: {
      from: new Date('2023/11/20'),
      to: 'today'
    },
    descriptions: [
      `Led the redesign of the company's e-commerce platform, improving user experience and increasing conversion rates by 30%.`,
      `Implemented advanced front-end features using React, resulting in a 40% reduction in page load times and enhanced site performance.`
    ]
  }
]

const filledEducation: StudentEducation[] = [
  {
    id: 'education-background-0',
    university: `King's College London`,
    level: "Bachelor's degree",
    field: 'Computer Science',
    time: {
      from: new Date('2018/01/20'),
      to: new Date('2023/10/20')
    },
    grade: 'Pass with Distinction',
    descriptions: [
      'Graduated with a Bachelor of Computer Science, achieving a GPA of 3.8 and completing a capstone project on machine learning algorithms.'
    ]
  }
]

const filledRanking: StudentRankItem[] = [
  {
    testId: 'html/css-2',
    scores: [98],
    rank: 5
  },
  {
    testId: 'js-1',
    scores: [92],
    rank: 83
  },
  {
    testId: 'js-2',
    scores: [85],
    rank: 83
  },
  {
    testId: 'py-1',
    scores: [78],
    rank: 191
  },
  {
    testId: 'cpp-1',
    scores: [24],
    rank: 1236
  },
  {
    testId: 'sql-1',
    scores: [64],
    rank: 786
  }
]

const filledSkills: StudentSkillMap[] = [
  {
    title: 'Front-end Development',
    breakdown: [
      {
        title: 'Coding skills',
        score: 10
      },
      {
        title: 'Framework & libraries',
        score: 9.8
      },
      {
        title: 'Network connection',
        score: 6.2
      },
      {
        title: 'Cross-browser & device',
        score: 7.9
      },
      {
        title: 'Responsive design',
        score: 8.8
      }
    ]
  },
  {
    title: 'Backend Development',
    breakdown: [
      {
        title: 'Data structure & algorithm',
        score: 4.3
      },
      {
        title: 'Server handling',
        score: 4.7
      },
      {
        title: 'Database & cache management',
        score: 1.5
      },
      {
        title: 'API knowledge',
        score: 2.2
      },
      {
        title: 'Frameworks & libraries',
        score: 3.0
      }
    ]
  },
  {
    title: 'UI / UX Design',
    breakdown: [
      {
        title: 'Wireframes & prototyping',
        score: 7
      },
      {
        title: 'Interaction design',
        score: 4.7
      },
      {
        title: 'Visual design',
        score: 10
      },
      {
        title: 'User research & usability',
        score: 2.2
      },
      {
        title: 'Information architecture',
        score: 8.0
      }
    ]
  }
]

export const students: Student[] = [
  {
    id: 'student-0',
    firstName: 'Demo',
    lastName: 'Student 1',
    email: 'demo-student-1@email.com',
    avatarUrl: '/images/database/students/student-temp0.png',
    courses: ['m1js', 'fcdm'],
    certificates: ['cert-0', 'cert-1', 'cert-2', 'cert-3'],
    profile: {
      about: {
        overview: filledOverview,
        contact: {
          location: {
            city: 'Subang Jaya',
            state: 'Selangor',
            country: 'Malaysia'
          },
          links: [
            {
              type: 'Github',
              url: 'https://www.github.com'
            },
            {
              type: 'LinkedIn',
              url: 'https://www.linkedin.com'
            },
            {
              type: 'Website',
              url: 'https://www.facebook.com'
            },
            {
              type: 'Behance',
              url: 'https://www.behance.com'
            }
          ]
        },
        workExperiences: filledWorkExperiences,
        educations: filledEducation
      },
      ranking: filledRanking,
      skills: filledSkills
    },
    settings: {
      path: 'Software Engineering',
      id: {
        type: 'NRIC',
        value: '123456'
      }
    }
  }
]
